import type { UserRole } from '@kainos/shared';
import { config } from '../config.js';
import { getAccessToken as getCognitoAccessToken } from './cognito.js';

/**
 * The signed-in person, as the app renders them.
 *
 * Cognito proves identity and holds the tokens; this is the KainOS half —
 * the name, the role and the organization, served by `GET /me`. It is mirrored
 * to `sessionStorage` so a reload paints the shell without waiting for that
 * call to come back.
 */
export interface SessionUser {
  id: string;
  fullName: string;
  email: string;
  jobTitle?: string;
  role: UserRole;
  tenantId: string;
  organizationName: string;
}

const STORAGE_KEY = 'kainos.session';

let current: SessionUser | null = null;

function readStorage(): SessionUser | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    // Private mode or blocked storage: fall back to memory only.
    return null;
  }
}

function writeStorage(user: SessionUser | null): void {
  try {
    if (user) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore: the in-memory copy still works for this tab.
  }
}

export function setSession(user: SessionUser): void {
  current = user;
  writeStorage(user);
}

export function clearSession(): void {
  current = null;
  writeStorage(null);
}

export function getCurrentUser(): SessionUser | null {
  current ??= readStorage();
  return current;
}

export function getActiveTenantId(): string | null {
  return getCurrentUser()?.tenantId ?? null;
}

/**
 * Bearer token for the next request.
 *
 * Async because Cognito may spend the refresh token to produce it, which is a
 * network call.
 */
export async function getAccessToken(): Promise<string | null> {
  if (config.useMocks) return 'demo-token';
  return getCognitoAccessToken();
}

/**
 * Reported when the API rejects a token the browser still believed in — a
 * revoked membership, or a refresh token that finally ran out.
 *
 * The HTTP client is the only thing that sees every response, but it cannot
 * navigate; `AuthProvider` subscribes here and turns it into a sign-out.
 */
type ExpiryListener = () => void;

let expiryListener: ExpiryListener | null = null;

export function onSessionExpired(listener: ExpiryListener): () => void {
  expiryListener = listener;
  return () => {
    if (expiryListener === listener) expiryListener = null;
  };
}

export function reportSessionExpired(): void {
  expiryListener?.();
}
